import React, { useCallback, useMemo, useState } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import startCase from 'lodash/startCase';
import { useSelector } from 'react-redux';

// GLOBAL FUNCTIONS
import { controlName } from 'functions';

// HOOKS
import useRecordView from 'helpers/getRecordView';

// CORE COMPONENTS
import Form from 'core/form/Form';

// VARIABLES
const RECIPIENT_OPTIONS = [
  {
    value: false,
    label: 'Select From List'
  },
  {
    value: true,
    label: 'Enter Manually'
  }
]
const DOCUMENT_TYPE_NAMES = {
  inventory: {
    1:  'Factory Invoice',
    2:  'Other',
    3:  'Purchase Invoice',
    4:  'Other',
    5:  'MSRP Sticker/Window Sticker',
    6:  'Bill of Lading',
    7:  'Bill of Lading',
    8:  'DX Documents',
    9:  'DX Documents',
    10: 'Inventory Detail',
    11: 'Inventory Detail',
    12: 'Repair Invoice',
    13: 'Repair Invoice',
    14: 'Purchase Appraisal',
    15: 'Photo Proof of VIN',
    16: 'Photo Proof of VIN',
    17: 'Photo Proof of Mileage',
    18: 'Photo Proof of Mileage',
    19: 'Trade Appraisal',
    20: 'MSRP Sticker/Window Sticker',
    21: 'Title in Possession',
    22: 'Vehicle Registration',
    23: 'Trade Inspection',
  },
  sales: {
    1:  'Purchase Invoice (SA1)',
    2:  'Executed Deal Package (SA2)',
    3:  'Accounting Invoice / Bill of Sale (SA3)',
    4:  'Other',
    5:  "Auction Invoice / WS Buyer's Order (SA2)",
    6:  'Other',
    7:  "Auction Invoice / WS Buyer's Order (SA2)",
    8:  'Other',
    9:  'Factory Invoice (SA1)',
    10: 'Executed Deal Package (SA2)',
    11: 'Accounting Invoice / Bill of Sale (SA3)',
    12: 'Other',
    13: 'MSRP',
    14: 'Factory Invoice (SA1)',
    15: 'Accounting Invoice / Bill of Sale (SA3)',
    16: 'Purchase Invoice (SA1)',
    17: 'Accounting Invoice / Bill of Sale (SA3)',
    18: 'Accounting Tag & Title Docs',
    19: 'Accounting Tag & Title Docs',
    20: 'Accounting Tag & Title Docs',
    21: 'Accounting Tag & Title Docs',
    22: 'Rebate Program Documents (Customer Signature)',
    23: 'Rebate Program Documents (Customer Signature)',
    24: 'Rebate Program Documents (Customer Signature)',
    25: 'Rebate Program Documents (Customer Signature)',
    26: 'F&I Products Sold Executed Agreements',
    27: 'F&I Products Sold Executed Agreements'
  }
}

// MAIN COMPONENT
const DocUprForm = ({
  documentType,
  uploadType,
  record,
  vehicle
}) => {

  // REDUX
  const { user } = useSelector(({ user }) => ({ user }));

  // FORMIK
  const { submitCount, values } = useFormikContext() || {};

  // CONTEXT
  const { view } = useRecordView();

  // STATE
  const [ manualEntry, setManualEntry ] = useState(false);

  // MEMOS
  const showError = useMemo(
    () => {
      if (!submitCount) return;
      if (!manualEntry) return;
      if (!!values.recipient?.email || !!values.recipient?.phone) return;
      return true;
    },
    [submitCount, values, manualEntry]
  )

  const customer = useMemo(() => {
    return [record.customer_first_name, record.customer_last_name].filter(c => !!c).join(' ');
  }, [record]);

  // CALLBACKS
  const handleRecipientChange = useCallback(
    e => {
      setManualEntry(e.target.value === 'true');
    },
    [setManualEntry]
  )

  // RENDER
  return (<>
    <Form.Col cols={{ xs: 24 }}>
      <p className="large">The information below will be sent along with the Upload Link for current document upload directly to the DSL. This link will expire in 48 hours.</p>
    </Form.Col>
    <Form.PlainText
      label="For"
      value={startCase(view !== 'inventory' ? customer : view)}
      plaintext
    />
    <Form.PlainText
      label="Stock #"
      value={`${vehicle.stock_num}`}
      plaintext
    />
    <Form.PlainText
      label="Vehicle"
      value={`${vehicle.make} ${vehicle.model} ${vehicle.vehicle_year}`}
      plaintext
    />
    <Form.PlainText
      label="Category"
      value={DOCUMENT_TYPE_NAMES[uploadType]?.[documentType]}
      plaintext
    />
    <Form.PlainText
      label="By"
      value={`${user.first_name} ${user.last_name}`}
      plaintext
    />
    <Form.Checklist
      name="manual_entry"
      type="radio"
      label="Recipient"
      defaultValue={false}
      value={manualEntry}
      options={RECIPIENT_OPTIONS}
      onChange={handleRecipientChange}
      required
    />
    <Form.Select
      name="staff_site_user_id"
      label="Select Recipient"
      lookup="allUsers"
      optionKeys={{
        label: 'name',
        value: 'site_user_id'
      }}
      required={!manualEntry}
      disabled={manualEntry}
    />
    <Form.Control
      key="firstName"
      name={controlName('first_name', 'recipient')}
      label="First Name"
      required={manualEntry}
      disabled={!manualEntry}
    />
    <Form.Control
      key="lastName"
      name={controlName('last_name', 'recipient')}
      label="Last Name"
      required={manualEntry}
      disabled={!manualEntry}
    />
    <Form.Control
      key="email"
      type="email"
      name={controlName('email', 'recipient')}
      label="Email"
      disabled={!manualEntry}
    />
    <Form.Control
      key="phone"
      type="tel"
      name={controlName('phone', 'recipient')}
      label="Cell Phone"
      disabled={!manualEntry}
    />
    {showError &&
      <Form.Col cols={{ xs: 24 }}>
        <p className="large text-danger">You must provide either a recipient email or a recipient cell phone.</p>
      </Form.Col>
    }
  </>)
}

// EXPORT
export default DocUprForm;
